import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CalendarBlank as CalendarIcon,
  CalendarCheck as CalendarCheckIcon,
  Plus as PlusIcon,
} from '@phosphor-icons/react';
import { toast } from '@/components/core/v2/toaster';

import { Seo } from '@/components/seo';

import { ReservationsCalendar } from './components/reservations-calendar';
import { MyReservations } from './components/my-reservations';
import { NewReservationDialog } from './components/new-reservation-dialog';

export function ReservationsPage(): React.JSX.Element {
  const [currentTab, setCurrentTab] = React.useState(0);
  const [showNewDialog, setShowNewDialog] = React.useState(false);
  const [preselectedAmenityId, setPreselectedAmenityId] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');

    if (payment === 'success') {
      toast.success('Deposit paid! Your reservation has been created.');
      setCurrentTab(1);
      void queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['blocked-slots'] });
    } else if (payment === 'cancel') {
      toast.info('Payment cancelled. No reservation was created.');
      setCurrentTab(0);
    }

    if (payment) {
      params.delete('payment');
      params.delete('reservation_id');
      const newQuery = params.toString();
      const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [queryClient]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleReserveAmenity = (amenityId: string) => {
    setPreselectedAmenityId(amenityId);
    setShowNewDialog(true);
  };

  const handleNewReservation = () => {
    setPreselectedAmenityId(null);
    setShowNewDialog(true);
  };

  return (
    <>
      <Seo title="Reservations" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        {/* Header */}
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            px: { xs: 2, md: 4 },
            py: 3,
          }}
        >
          <Container disableGutters maxWidth="xl">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.35rem' }}>Reservations</Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
                  Start a new reservation, check availability, or review your bookings
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<PlusIcon size={18} weight="bold" />}
                onClick={handleNewReservation}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2.5,
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                Start Reservation
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Tabs */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container disableGutters maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 48,
                },
              }}
            >
              <Tab icon={<CalendarIcon size={18} />} iconPosition="start" label="Availability Calendar" />
              <Tab icon={<CalendarCheckIcon size={18} />} iconPosition="start" label="My Reservations" />
            </Tabs>
          </Container>
        </Box>

        {/* Content */}
        <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 4 } }}>
          {/* Mobile FAB */}
          <Button
            variant="contained"
            startIcon={<PlusIcon size={18} weight="bold" />}
            onClick={handleNewReservation}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              mb: 2,
              display: { xs: 'flex', sm: 'none' },
            }}
          >
            Start Reservation
          </Button>

          {currentTab === 0 && (
            <ReservationsCalendar onReserve={handleReserveAmenity} />
          )}
          {currentTab === 1 && (
            <MyReservations />
          )}
        </Container>
      </Box>

      <NewReservationDialog
        open={showNewDialog}
        onClose={() => {
          setShowNewDialog(false);
          setPreselectedAmenityId(null);
        }}
        onSuccess={() => {
          setShowNewDialog(false);
          setPreselectedAmenityId(null);
          void queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
          void queryClient.invalidateQueries({ queryKey: ['blocked-slots-all'] });
        }}
        preselectedAmenityId={preselectedAmenityId}
      />
    </>
  );
}
